import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactDOM from 'react-dom/client';

const Modal = ({ 
  isOpen, 
  onClose, 
  onOpen,
  children, 
  title,
  size = 'medium',
  footer,
  closeOnOverlay = true
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const modalContentRef = useRef(null);
  const hasCalledOnOpen = useRef(false);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose?.();
      hasCalledOnOpen.current = false;
    }, 300);
  }, [onClose]);

  // Handle open/close animations and body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Trigger animation after paint
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Call onOpen after DOM is ready
  useEffect(() => {
    if (isOpen && isAnimating && modalContentRef.current && !hasCalledOnOpen.current) {
      // Wait for next frame to ensure DOM is fully rendered and accessible
      requestAnimationFrame(() => {
        if (modalContentRef.current && document.body.contains(modalContentRef.current)) {
          hasCalledOnOpen.current = true;
          onOpen?.();
        }
      });
    }
  }, [isOpen, isAnimating, onOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getSizeClass = () => {
    const sizeMap = {
      fullScreen: 'modal__container--fullscreen',
      large: 'modal__container--large',
      medium: 'modal__container--medium',
      small: 'modal__container--small',
      auto: 'modal__container--auto'
    };
    return sizeMap[size] || sizeMap.medium;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={`modal ${isAnimating ? 'modal--open' : 'modal--closing'}`}>
      <div className="modal__overlay" onClick={handleOverlayClick} />
      <div className={`modal__container ${getSizeClass()}`}>
        <div className="modal__header">
          {title && <h2 className="modal__title">{title}</h2>}
          <button 
            className={`modal__close ${!title ? 'modal__close--only' : ''}`}
            onClick={handleClose}
            aria-label="Close modal"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div ref={modalContentRef} className="modal__content">
          {children}
        </div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;

export function initGlobalModal() {
  if (window.SharedComponents?.modalManager) {
    return window.SharedComponents.modalManager;
  }

  let modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    modalRoot = document.createElement('div');
    modalRoot.id = 'modal-root';
    document.body.appendChild(modalRoot);
  }

  const stateRefs = {
    setIsOpen: null,
    setModalState: null,
    isOpen: false
  };

  const modalManager = {
    open: (options = {}) => {
      if (stateRefs.setModalState && stateRefs.setIsOpen) {
        stateRefs.setModalState({
          content: options.content || null,
          title: options.title || null,
          size: options.size || 'medium',
          footer: options.footer || null,
          onOpen: options.onOpen || null,
          onClose: options.onClose || null,
          closeOnOverlay: options.closeOnOverlay ?? true
        });
        stateRefs.setIsOpen(true);
      }
    },
    close: () => stateRefs.setIsOpen?.(false),
    isOpen: () => stateRefs.isOpen
  };

  const GlobalModalManager = () => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [modalState, setModalState] = React.useState({
      content: null,
      title: null,
      size: 'medium',
      footer: null,
      onOpen: null,
      onClose: null,
      closeOnOverlay: true
    });

    React.useEffect(() => {
      stateRefs.setIsOpen = setIsOpen;
      stateRefs.setModalState = setModalState;
      stateRefs.isOpen = isOpen;
    }, [isOpen]);

    const handleClose = () => {
      modalState.onClose?.();
      setIsOpen(false);
    };

    const renderContent = () => {
      if (!modalState.content) return null;
      
      if (typeof modalState.content === 'string') {
        return React.createElement('div', {
          dangerouslySetInnerHTML: { __html: modalState.content }
        });
      }
      
      return modalState.content;
    };

    return React.createElement(Modal, {
      isOpen,
      onClose: handleClose,
      onOpen: modalState.onOpen,
      title: modalState.title,
      size: modalState.size,
      footer: modalState.footer,
      closeOnOverlay: modalState.closeOnOverlay
    }, renderContent());
  };

  const root = ReactDOM.createRoot(modalRoot);
  root.render(React.createElement(GlobalModalManager));

  // Store in window for reuse
  window.SharedComponents = window.SharedComponents || {};
  window.SharedComponents.modalManager = modalManager;

  return modalManager;
}